import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Building2,
  Upload,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  X,
  FileStack,
} from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Card, CardContent } from "@/components/ui/card";

interface OnboardingWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

type OnboardingStep = 0 | 1 | 2 | 3;

export function OnboardingWizard({
  open,
  onClose,
  onComplete,
}: OnboardingWizardProps) {
  const [step, setStep] = useState<OnboardingStep>(0);
  const [, setLocation] = useLocation();

  // Step 1: Company Setup
  const [companyName, setCompanyName] = useState("");
  const [uid, setUid] = useState("");
  const [legalForm, setLegalForm] = useState("AG");
  const [useSampleData, setUseSampleData] = useState(false);
  const [createdCompanyId, setCreatedCompanyId] = useState<number | null>(null);

  // Step 2: Transaction Import
  const [loadingSampleTransactions, setLoadingSampleTransactions] =
    useState(false);
  const [importedCount, setImportedCount] = useState(0);

  const createCompanyMutation = trpc.company.create.useMutation();
  const loadSampleTransactionsMutation =
    trpc.onboarding.loadSampleTransactions.useMutation();
  const completeOnboardingMutation = trpc.onboarding.complete.useMutation();

  const progress = step === 0 ? 0 : (step / 3) * 100;

  const handleStartDiscovery = () => {
    onClose();
    setLocation("/company-discovery");
  };

  const handleCreateCompany = async () => {
    if (!companyName) {
      toast.error("Please enter a company name");
      return;
    }

    try {
      const company = await createCompanyMutation.mutateAsync({
        name: companyName,
        uid: uid || undefined,
        legalForm: legalForm || undefined,
        kontenplanTemplate: "kmu",
      });

      setCreatedCompanyId(company.id);
      toast.success("Company created successfully!");
      setStep(2);
    } catch (error) {
      toast.error("Failed to create company");
      console.error(error);
    }
  };

  const handleLoadSampleTransactions = async () => {
    if (!createdCompanyId) return;

    setLoadingSampleTransactions(true);
    try {
      const result = await loadSampleTransactionsMutation.mutateAsync({
        companyId: createdCompanyId,
      });

      setImportedCount(result.importedCount);
      toast.success(`Loaded ${result.importedCount} sample transactions!`);
      setStep(3);
    } catch (error) {
      toast.error("Failed to load sample transactions");
      console.error(error);
    } finally {
      setLoadingSampleTransactions(false);
    }
  };

  const handleSkipImport = () => {
    setStep(3);
  };

  const handleComplete = async () => {
    if (!createdCompanyId) return;

    try {
      await completeOnboardingMutation.mutateAsync({
        companyId: createdCompanyId,
      });

      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });

      toast.success("Welcome to your Swiss Tax Platform!");
      onComplete();
      onClose();
      setLocation("/dashboard");
    } catch (error) {
      toast.error("Failed to complete onboarding");
      console.error(error);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">
                How would you like to get started?
              </h3>
              <p className="text-sm text-muted-foreground">
                Choose the method that works best for you
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Document Discovery Option */}
              <Card
                className="cursor-pointer hover:border-primary transition-colors group"
                onClick={handleStartDiscovery}
              >
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <FileStack className="h-7 w-7 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">
                        Import from Documents
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Drop your bank statements, tax filings, or invoices.
                        We'll automatically extract company info, vendors, and
                        customers.
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-primary">
                      <Sparkles className="h-3 w-3" />
                      <span>AI-Powered</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Manual Setup Option */}
              <Card
                className="cursor-pointer hover:border-primary transition-colors group"
                onClick={() => setStep(1)}
              >
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <Building2 className="h-7 w-7 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Set Up Manually</h4>
                      <p className="text-sm text-muted-foreground">
                        Enter your company details manually. Perfect if you
                        don't have documents ready or prefer a guided setup.
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span>Quick Setup</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-center pt-4">
              <Button variant="ghost" onClick={onClose}>
                <X className="mr-2 h-4 w-4" />
                Skip for now
              </Button>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Company Setup</h3>
                <p className="text-sm text-muted-foreground">
                  Create your first company profile
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="companyName">Company Name *</Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  placeholder="e.g., Habitusnet AG"
                  className="mt-1.5"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="uid">Swiss UID</Label>
                  <Input
                    id="uid"
                    value={uid}
                    onChange={e => setUid(e.target.value)}
                    placeholder="CHE-123.456.789"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="legalForm">Legal Form</Label>
                  <Select value={legalForm} onValueChange={setLegalForm}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AG">AG (Corporation)</SelectItem>
                      <SelectItem value="GmbH">GmbH (LLC)</SelectItem>
                      <SelectItem value="Einzelfirma">
                        Einzelfirma (Sole Proprietorship)
                      </SelectItem>
                      <SelectItem value="Verein">
                        Verein (Association)
                      </SelectItem>
                      <SelectItem value="Stiftung">
                        Stiftung (Foundation)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-2 rounded-lg border p-4 bg-accent/50">
                <Checkbox
                  id="sampleData"
                  checked={useSampleData}
                  onCheckedChange={checked =>
                    setUseSampleData(checked as boolean)
                  }
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="sampleData"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Use sample data to explore
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    We'll load 50 realistic Swiss transactions to help you
                    explore the platform
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={() => setStep(0)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleCreateCompany}
                disabled={createCompanyMutation.isPending}
              >
                {createCompanyMutation.isPending ? "Creating..." : "Next"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Upload className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  Import First Transactions
                </h3>
                <p className="text-sm text-muted-foreground">
                  Get started with your financial data
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium mb-1">
                      Load Sample Swiss Transactions
                    </h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      We'll create 50 realistic Swiss transactions including:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1.5 ml-4">
                      <li>• Salary payments and social security</li>
                      <li>• Office rent and utilities</li>
                      <li>• Supplier invoices with Swiss VAT</li>
                      <li>• Bank fees and interest</li>
                      <li>• Customer payments and receivables</li>
                    </ul>
                    <Button
                      onClick={handleLoadSampleTransactions}
                      disabled={loadingSampleTransactions}
                      className="mt-4 w-full"
                    >
                      {loadingSampleTransactions
                        ? "Loading..."
                        : "Load Sample Transactions"}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="text-center text-sm text-muted-foreground">
                <p>or</p>
              </div>

              <div className="rounded-lg border p-6">
                <h4 className="font-medium mb-2">Import Your Own CSV</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  You can import your bank statements later from the Import page
                </p>
                <Button
                  variant="outline"
                  onClick={() => setLocation("/bank-import")}
                  className="w-full"
                >
                  Go to Import Page
                </Button>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button variant="outline" onClick={handleSkipImport}>
                Skip this step
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--success)]/10">
                <CheckCircle2 className="h-6 w-6 text-[var(--success)]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">You're All Set!</h3>
                <p className="text-sm text-muted-foreground">
                  Ready to manage your Swiss taxes
                </p>
              </div>
            </div>

            <div className="rounded-lg border p-6 bg-accent/50 space-y-4">
              <h4 className="font-medium">What's Next?</h4>
              <div className="space-y-3">
                {importedCount > 0 && (
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[var(--success)] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">
                        Review AI-Categorized Transactions
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {importedCount} transactions have been automatically
                        categorized using Swiss accounting rules
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[var(--success)] mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Explore the Dashboard</p>
                    <p className="text-sm text-muted-foreground">
                      See your financial overview, categorization analytics, and
                      compliance status
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[var(--success)] mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">
                      Import More Transactions
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Upload your CSV bank statements with smart column mapping
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[var(--success)] mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">
                      Set Up Categorization Rules
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Create custom rules for automatic transaction
                      categorization
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleComplete}
                disabled={completeOnboardingMutation.isPending}
              >
                {completeOnboardingMutation.isPending
                  ? "Completing..."
                  : "Go to Dashboard"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Welcome to Swiss Corporate Tax Platform</DialogTitle>
          <DialogDescription>
            Let's get you set up in 3 simple steps
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress Bar - only show after initial choice */}
          {step > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Step {step} of 3</span>
                <span>{Math.round(progress)}% complete</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Step Content */}
          {renderStep()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
